import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGet, usePost } from "../../../common/api-service";
import { PagedUserModel } from "./types";
import { UserSchema } from "./schema";
import { useSnackbar } from "notistack";

export const useGetPagedUsersByFacility = (pageNumber: number, pageSize: number, facilityCode: string) => {
    const { get } = useGet();
    var url = `/api/users/facility-users?FacilityCode=${facilityCode}&PageNumber=${pageNumber}&PageSize=${pageSize}`;

    return useQuery<PagedUserModel>({
        queryFn: () => get<PagedUserModel>(url),
        queryKey: [url],
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: 0,
        // enabled: false
    });
};

export const useCreateFacilityUser = () => {
    const { post } = usePost();
    return useMutation({
        mutationFn: (data: UserSchema) => {
            return post(`/api/users/facility-user`, data);
        },
    });
};

export const useSendEmailVerificationRequest = () => {
    const { post } = usePost();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: (data: { email: string }) => {
            return post(`/api/user/send-email-verification`, data, false);
        },
        onSuccess: () => {
            enqueueSnackbar("A verification link has been sent to the user's email address.", { variant: "success" })
        }
    });
};

// --- Admin: enforce authenticator MFA on a user (feature/totp-mfa) ---
export interface AdminAuthenticatorStatus {
    userId: string;
    email: string;
    enabled: boolean;
    required: boolean;
    eligible: boolean;
}

export const useUserAuthenticatorAdminStatus = (userId: string, open: boolean) => {
    const { get } = useGet();
    return useQuery<AdminAuthenticatorStatus>({
        queryKey: ["admin-authenticator-status", userId],
        queryFn: () => get(`/api/user/mfa/authenticator/admin/status?userId=${encodeURIComponent(userId)}`),
        enabled: open && !!userId,
        staleTime: 0,
    });
};

export const useSetAuthenticatorRequired = () => {
    const { post } = usePost();
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation<AdminAuthenticatorStatus, Error, { userId: string; required: boolean }>({
        mutationFn: (data) =>
            post(`/api/user/mfa/authenticator/admin/require`, { UserId: data.userId, Required: data.required }, false),
        onSuccess: (d) => {
            queryClient.invalidateQueries({ queryKey: ["admin-authenticator-status", d.userId] });
            enqueueSnackbar(
                d.required
                    ? "Authenticator is now required for this user."
                    : "Authenticator requirement removed.",
                { variant: "success" },
            );
        },
        onError: (e: any) => {
            const msg = typeof e?.response?.data === "string" ? e.response.data : "Failed to update.";
            enqueueSnackbar(msg, { variant: "error" });
        },
    });
};
